import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import _ from 'lodash';
import { createPortal, unstable_batchedUpdates as batchedUpdates } from 'react-dom';
import PropTypes from 'prop-types';
import cx from 'classnames';
import { Trans, useTranslation } from 'react-i18next';

import Modal from '../../common/Modal';
import Icon from '../../common/Icon';
import Dropdown, { DropdownDivider, DropdownItem } from '../../common/Dropdown/Dropdown';
import InstrumentRow from '../../common/InstrumentRow';
import ModalButton from '../../common/ModalButton';
import ModalAction from '../../common/ModalAction';
import PromptModal from '../PromptModal';
import ChordBarsTransport, { ChordBarsMixer } from '../../ChordBarsTransport';
import ChordLibrarySheet from '../../ChordLibrarySheet';
import MidiPiano from '../../MidiPiano';
import RestMark from '../../RestMark';
import BarEditor from './BarEditor';

import useChordAudition from '../../../hooks/useChordAudition';
import useChordBarsPreview from '../../../hooks/useChordBarsPreview';
import useFontSize from '../../../hooks/useFontSize';
import useSoundingChord from '../../../hooks/useSoundingChord';
import useBooleanSetting from '../../../hooks/useBooleanSetting';
import useEditHistory from '../../../hooks/useEditHistory';
import useConfirmClick from '../../../hooks/useConfirmClick';
import useCopiedBar from '../../../hooks/useCopiedBar';
import useCopiedChart from '../../../hooks/useCopiedChart';
import useTapRhythm from '../../../hooks/useTapRhythm';
import { KEYBOARD_SHEET } from '../../../hooks/useSpacePlay';
import {
  addBar,
  addLine,
  cellChordBeats,
  cellChords,
  cellMissingBeats,
  clearCell,
  countGridBars,
  createGrid,
  DEFAULT_BARS_PER_LINE,
  duplicateLine,
  fillLine,
  fillLines,
  findChordLines,
  formatBarLines,
  getSoundingChord,
  insertLine,
  isChordToken,
  isMelodyCell,
  lineAfterMove,
  lyricsMatchChart,
  MAX_BARS_PER_LINE,
  moveBar,
  moveChord,
  moveLine,
  opensSection,
  parseBarLines,
  removeBar,
  reorderChord,
  removeEmptyLines,
  removeLine,
  sectionsForLines,
  sectionsAfterAddLine,
  sectionsAfterDuplicateLine,
  sectionsAfterMoveLine,
  sectionsAfterRemoveLine,
  setCellMelody,
  swapBars,
  toggleSection,
  wrapLines,
  writeChord,
} from '../../../utils/chordBars';
import {
  alignTaps,
  barStep,
  entryBeats,
  holdSlice,
  nextMissing,
  placePick,
  removeSlice,
  readBarSlices,
  replaceCell,
  restSlice,
  setEntry,
  setSliceChord,
  sliceCell,
  slicesFromTaps,
} from '../../../utils/barSlices';
import { chartDataToGrid, gridToChartData } from '../../../utils/chartData';
import { copyBar, copyChart } from '../../../utils/chordBarsClipboard';
import { getChordMidiNotes, getChordRhythmBass, getMelodyNote } from '../../../utils/chordBarsMidi';
import { DISPLAY_CHORD_REGEX, isChordLine, isLyricsLine } from '../../../utils/chordPatterns';
import { chordEm, REST_EM } from '../../../utils/chordGlyphs';
import { getChordFamily } from '../../../utils/chordLibrary';
import { BLOCK_CHORDS, toRhythmId } from '../../../utils/chordRhythmPatterns';
import { INVERSION_ORDINALS, splitInversion } from '../../../utils/chordNotes';
import { inversionChords, inversionIndex, nextInversionChord } from '../../../utils/chordVoicing';
import { getChordQuality, getDiatonicChords } from '../../../utils/diatonicChords';
import { getHistoryShortcut } from '../../../utils/editHistory';
import { formatNumber } from '../../../utils/format';
import {
  DEFAULT_INSTRUMENT_ID,
  getChartInstrumentId,
  getChartInstrumentOverride,
} from '../../../utils/midiInstruments';
import { isSameKey, spellKey } from '../../../utils/partKey';
import { settingKeys } from '../../../utils/settings';
import { getBeatsPerBar, getSecondsPerBar } from '../../../utils/songTiming';
import { createScopedSortable } from '../../../utils/sorting';

import { ReactComponent as PlayIcon } from '../../../icons/play-solid.svg';
import { ReactComponent as SlidersIcon } from '../../../icons/sliders.svg';
import { ReactComponent as StopIcon } from '../../../icons/square-rounded.svg';
import { ReactComponent as TrashIcon } from '../../../icons/trash.svg';
import { ReactComponent as LibraryIcon } from '../../../icons/dots-grid3x3.svg';
import { ReactComponent as CheckIcon } from '../../../icons/check.svg';
import { ReactComponent as PlusIcon } from '../../../icons/plus-circle.svg';
import { ReactComponent as AddChordIcon } from '../../../icons/plus.svg';
import { ReactComponent as RemoveBarIcon } from '../../../icons/xmark-circle.svg';
import { ReactComponent as CopyIcon } from '../../../icons/copy.svg';
import { ReactComponent as FillLineIcon } from '../../../icons/fill-bar.svg';
import { ReactComponent as PasteIcon } from '../../../icons/paste-clipboard.svg';
import { ReactComponent as SectionIcon } from '../../../icons/section-break.svg';
import { ReactComponent as DragIcon } from '../../../icons/drag-dots.svg';
import { ReactComponent as EllipsisVertical } from '../../../icons/more-vert.svg';
import { ReactComponent as UndoIcon } from '../../../icons/undo.svg';
import { ReactComponent as RedoIcon } from '../../../icons/redo.svg';

import './ChordBarsModal.scss';

// How long a finger has to rest on a bar before it is dragging it rather than tapping it. Short
// enough not to feel like a wait, long enough that a tap meant to move the cursor never travels.
// Touch only — see the row sortable, where a mouse takes no delay at all.
const DRAG_DELAY_MS = 180;
// How far a finger may travel during that wait without the press being read as a scroll instead.
// A row of bars scrolls sideways and the modal scrolls down, so a real swipe has to be able to
// call the drag off — but a finger settling onto a bar moves a few pixels and means to stay.
const DRAG_JITTER_PX = 8;
// How far a press has to travel before it is a drag at all. Unlike the wait above this one only
// holds the drag back rather than calling it off, so a bar picks up the moment it is really moved
// and a press that stays put stays a tap.
const DRAG_TRAVEL_PX = 8;
// How long after a drop a click still counts as the end of that drag
const DROP_CLICK_MS = 400;
// How long Copy shows its check
const COPIED_MS = 1200;

/* ---------------------------------------------------------------------------
 * Saying how long a chord lasts
 *
 * The chart itself only shows it: a chord's box is as wide as the beats it holds, which reads at a
 * glance and says nothing exact. Fine in View, where a chart is being played from. Not enough here,
 * where it is being written — "is that two beats or three" is the whole question the builder exists
 * to answer, and a width cannot answer it.
 * ------------------------------------------------------------------------- */

// A beat count as it is written on a cell. Whole beats nearly always, so the halves and quarters a
// finer bar parses to are the only place a decimal shows up at all.
const formatBeats = (beats) => formatNumber(Math.round(beats * 100) / 100);

/*
 * Fitting a bar's chords into the bar.
 *
 * Every bar on a line takes the same width, and four chords sharing one — "Dm Dm Dm Dm" — are wider
 * than that on a phone. Left alone the bar widened for them, which pushed its bar lines out of step
 * with the line above and the rest of the line off the edge. So, as ChordBarsChart does in View, the
 * bar keeps its width and what is written in it is set smaller: each cell is a size container, and
 * the stylesheet divides its width by what the chords need at full size (--cell-em) to get the size
 * they can have, clamped to a floor past which a symbol stops being readable.
 */
// .cell-beats is set at this share of the chord's size, so it shrinks along with the chord above it
const BEATS_SCALE = 0.69;
// A tie's rule: the 0.35em it stops short of the next chord, and enough rule left to read as one
const TIE_EM = 1;
// The glyph widths are rounded up already; this is for the ellipsis, which shows at a pixel short
const CELL_SLACK_EM = 0.2;

const cellFit = (cell, beatsPerBar) => {
  const held = cellChordBeats(cell, beatsPerBar);

  if (held.length === 0) return undefined;

  const em = held.reduce((total, { chord, beats, missing, rest }) => {
    // The stack is as wide as the wider of the symbol and the count under it — see .cell-chord-stack
    const count = held.length > 1 && beats > 0 ? chordEm(formatBeats(beats)) * BEATS_SCALE : 0;

    if (missing) return total + Math.max(TIE_EM, count);
    if (rest) return total + Math.max(REST_EM, count);
    if (chord === null) return total + TIE_EM;

    return total + Math.max(chordEm(chord), count);
  }, CELL_SLACK_EM);

  return { '--cell-em': Number(em.toFixed(2)), '--cell-gaps': held.length - 1 };
};

/**
 * The bar's wash in its chords' Chord Library colours: none for the key's own, a flat tint for one
 * borrowed chord, and a gradient across a shared bar with each colour centred on its chord's beats.
 */
const cellShade = (cell, beatsPerBar, familyOf) => {
  const held = cellChordBeats(cell, beatsPerBar);
  const total = held.reduce((sum, { beats }) => sum + (beats || 1), 0);
  let start = 0;

  const stops = held.map(({ chord, beats }) => {
    const family = chord ? familyOf(chord) : null;
    const centre = ((start + (beats || 1) / 2) / total) * 100;

    start += beats || 1;

    return {
      colour:
        family && family !== 'key' ? `color-mix(in srgb, var(--cb-family-${family}) 14%, transparent)` : 'transparent',
      centre,
    };
  });

  if (stops.every(({ colour }) => colour === 'transparent')) return undefined;

  if (stops.length === 1) return { '--cell-shade': `linear-gradient(${stops[0].colour}, ${stops[0].colour})` };

  return {
    '--cell-shade': `linear-gradient(90deg, ${stops.map(({ colour, centre }) => `${colour} ${centre.toFixed(1)}%`).join(', ')})`,
  };
};

// The bar in words, for the cell's tooltip: what the drawing shows, said outright
const cellTitle = (t, cell, beatsPerBar) => {
  const held = cellChordBeats(cell, beatsPerBar);

  if (held.length === 0) return t('dialogs.chordBars.cell.held');
  if (held.length === 1 && held[0].rest) return t('dialogs.chordBars.cell.restBar');
  if (held.length === 1 && held[0].chord !== null) {
    return t('dialogs.chordBars.cell.chordBar', { chord: held[0].chord });
  }

  return held
    .map(({ chord, beats, missing, rest }) => {
      const count = { count: beats, beats: formatBeats(beats) };

      if (missing) return t('dialogs.chordBars.cell.missing', count);
      if (rest) return t('dialogs.chordBars.cell.restFor', count);

      // A tie has no symbol of its own — what it is holding was written in the bar before it
      return chord === null
        ? t('dialogs.chordBars.cell.heldFor', count)
        : t('dialogs.chordBars.cell.chordFor', { ...count, chord });
    })
    .join(', ');
};

// How long after a letter key another press of it still means "not that one, the next one" rather
// than "write it again". Long enough to be reached deliberately, short enough that coming back to a
// chord after thinking about it writes a second one.
const CYCLE_MS = 600;

// The most bars one tapped rhythm runs across
const MAX_TAP_BARS = 32;

const formatSeconds = (seconds) => {
  const total = Math.round(seconds);

  return total >= 60 ? `${Math.floor(total / 60)}:${String(total % 60).padStart(2, '0')}` : `${total}s`;
};

// The chord still ringing as a bar starts: the one sounding at the end of the bar before, if any
const chordBefore = (lines, { line, bar }) => {
  if (bar > 0) return getSoundingChord(lines, { line, bar: bar - 1 });

  for (let above = line - 1; above >= 0; above -= 1) {
    if (lines[above].length > 0) return getSoundingChord(lines, { line: above, bar: lines[above].length - 1 });
  }

  return '';
};

/** The rule that opens a section — the same reading ChordBarsChart makes. */
const sectionClass = (sections, line) => ({
  'grid-line--section-start': line > 0 && (sections[line] ?? 0) !== (sections[line - 1] ?? 0),
});

/**
 * One chord row, cut into its chords and the spacing between them.
 *
 * The chords are picked out as `.chord` because that is the node useSoundingChord counts to find
 * the one being played — the same class View writes them as, so the chord lit here is lit the way
 * it is lit on the page. A fresh regex per row: the exported one is global and carries its
 * lastIndex from wherever it was last used.
 */
const chordRow = (row) => {
  const pattern = new RegExp(DISPLAY_CHORD_REGEX);
  const parts = [];
  let at = 0;
  let match = pattern.exec(row);

  while (match) {
    if (match.index > at) parts.push(row.slice(at, match.index));

    parts.push(
      <span className="chord" key={match.index}>
        {match[0]}
      </span>,
    );
    at = match.index + match[0].length;
    match = pattern.exec(row);
  }

  if (at < row.length) parts.push(row.slice(at));

  return parts;
};

/**
 * The part's words while its chart is written, beside the grid rather than in place of it, with the
 * chord being played lit where it is written over them.
 *
 * Read-only, and deliberately so: what a bar is worth is being decided here, and the line being
 * sung is the thing it has to fit — so the words are here to be counted against, not edited. The
 * part's own field is where they are written.
 *
 * Rendered as the text it is stored as rather than as View marks it up. A chord sits over its
 * syllable by the spaces in front of it, so the spacing is the whole of what this has to keep — the
 * rows go out `pre`, and the chords are picked out of a chord row so the player has somewhere to
 * put the highlight.
 *
 * Playback scrolls this box and nothing above it (see useSoundingChord): the grid is what is being
 * worked on, and a chord landing shouldn't scroll it off the screen.
 */
const LyricsPreview = ({ rows, currentChord, currentChordSeconds }) => {
  const ref = useSoundingChord(currentChord, currentChordSeconds, rows, { contain: true });
  // At the size the reader set the words to in View, as the player's Lyrics view does. The builder
  // zooms itself on big monitors (--cb-zoom), so that is divided back out to land on View's size.
  const fontSize = useFontSize();

  return (
    <div className="lyrics-preview" style={{ fontSize: `calc(${fontSize} / var(--cb-zoom, 1))` }} ref={ref}>
      {rows.map((row, index) => {
        const isChords = isChordLine(row);

        return (
          // Rows have no identity of their own — nothing here is edited or reordered
          // eslint-disable-next-line react/no-array-index-key
          <div className={cx('lyrics-row', { 'lyrics-row--chords': isChords })} key={index}>
            {/* A blank row is the gap between two stanzas, and an empty box has no height to be one */}
            {row === '' && '\u00a0'}
            {row !== '' && (isChords ? chordRow(row) : row)}
          </div>
        );
      })}
    </div>
  );
};

LyricsPreview.propTypes = {
  rows: PropTypes.arrayOf(PropTypes.string).isRequired,
  currentChord: PropTypes.number.isRequired,
  currentChordSeconds: PropTypes.number.isRequired,
};

/**
 * Builds a bar-line chord chart — "| C |   | B |  |   | B | G |".
 *
 * The notation already means something to the app: countBars reads it, and it is the one thing
 * that gives a chords-only part an exact length instead of an estimate. What was missing was any
 * way to write it other than typing pipes by hand, and any way to check the result short of
 * playing the song. So the grid here is the chart, and the player is the check — chords and click
 * together, at the song's own tempo, before a single line is committed to the part.
 *
 * @param {object} song - { key, bpm, timeSignature, category, chartInstrumentId, chartRhythm },
 *   straight off the song form. `bpm` is the part's own tempo where it has one; `category` is the
 *   category's name, for the drums; the last two are what the part plays with unless it says otherwise
 * @param {number|string} songBpm - The song's BPM, shown beside a part tempo that differs from it
 * @param {string} partKey - The key the part is in, where a part carries one of its own or inherits
 *   one. The palette and library are built from it
 * @param {string} lyrics - The part's current text, for chords to import and words to preview
 * @param {boolean} isAttached - Whether the part is made of words, which its chart sits beside.
 *   Nothing is written back over its lyrics, so the chords in them can be imported as they are
 *   written, words and all; an instrumental's imported chord lines are replaced by the chart
 * @param {object} chart - The part's chart as chartData, to edit, where it has one
 * @param {string} instrumentId - The instrument the part's chart is stored with, if any
 * @param {string} rhythm - The rhythm the part's chart is stored with, if any
 * @param {object} melody - The part's MIDI to play along,
 *   `{ midiJson, instrumentId, semitones, atBar }` — `atBar` where the clip belongs partway into a
 *   chart shared between instruments
 * @param {function} onInsert - Handed the chart as chartData, `{ instrumentId, rhythm }` the part
 *   sets over the song's (null where it follows it), and `{ replaceChords }` when the chart was
 *   imported from the part's written chords
 * @param {function} onRemove - Takes the chart off the part
 */
// The grid the builder opens on: the part's chart, or one empty line. A chart saved before lines
// were capped opens wrapped, and is saved that way.
const openingGrid = (existing) => {
  const opened = existing?.lines?.length ? wrapLines(existing.lines, existing.sections) : null;

  return {
    lines: opened?.lines ?? createGrid(1, DEFAULT_BARS_PER_LINE),
    sections: opened?.sections ?? [0],
    countInEntry: Boolean(existing?.countInEntry),
  };
};

const ChordBarsModal = ({
  isOpen,
  onClose,
  onInsert,
  onRemove,
  partLabel,
  song,
  partKey,
  songBpm,
  lyrics,
  isAttached = false,
  chart = null,
  instrumentId: storedInstrumentId,
  rhythm: storedRhythm = null,
  melody = null,
}) => {
  const { t } = useTranslation();
  const [lines, setLines] = useState(() => createGrid(1, DEFAULT_BARS_PER_LINE));
  // Which section each line belongs to, one entry per line — held beside the grid rather than in it
  // so every line edit is the same splice applied twice and the two cannot fall out of step
  const [sections, setSections] = useState(() => [0]);
  // Whether the first bar sounds over the count-in, coming in where the rest it opens on ends
  const [countInEntry, setCountInEntry] = useState(false);
  const [position, setPosition] = useState({ line: 0, bar: 0 });
  const [custom, setCustom] = useState('');
  // The palette's + and the "Any chord" field it opens into are one control that morphs between the
  // two. 'closing' keeps the field mounted while it shrinks back to the +'s size and crossfades its
  // text for a + of its own, so by the time the real + replaces it there is nothing left to change.
  const [customMode, setCustomMode] = useState('closed');
  const isCustomOpen = customMode === 'open' || customMode === 'closing';
  // Whether the chart makes a sound when it is touched. Off to begin with: a builder that plays a
  // chord at every keystroke is the wrong default in a rehearsal room, and turning it on is one tap.
  const [isAuditioning, setIsAuditioning] = useState(false);
  // The rhythm and instrument the part sets over the song's, null where it follows them. Handed
  // back on insert. A song with no rhythm of its own plays block chords.
  const [rhythmOverride, setRhythmOverride] = useState(storedRhythm);
  const songRhythm = song?.chartRhythm || BLOCK_CHORDS;
  const [instrumentOverride, setInstrumentOverride] = useState(() =>
    getChartInstrumentOverride({ chartInstrumentId: storedInstrumentId }),
  );
  const instrumentId = getChartInstrumentId({ chartInstrumentId: instrumentOverride }, song);
  const songInstrumentId = getChartInstrumentId(null, song);
  // Picking what the song already plays follows it. The app's default can't be set over a song's
  // either — see getChartInstrumentOverride.
  const pickInstrument = (id) =>
    setInstrumentOverride(id === songInstrumentId || id === DEFAULT_INSTRUMENT_ID ? null : id);
  const pickRhythm = (choice) => setRhythmOverride(choice === songRhythm ? null : choice);
  const [isLibraryOpen, setIsLibraryOpen] = useState(false);
  const [isMixerOpen, setIsMixerOpen] = useState(false);
  const closeLibrary = useCallback(() => setIsLibraryOpen(false), []);

  // The element the lines live in, and a key that remounts it. SortableJS moves the dragged row in
  // the DOM itself, and the grid is keyed by index — so React would go on believing the node it
  // built for index 0 is still there, and paint the reordered lines into the wrong rows. Bumping
  // the key after each drop hands the DOM back to React, rebuilt from the order state now holds.
  const [linesRef, setLinesRef] = useState(null);
  const [gridKey, setGridKey] = useState(0);

  // The cursor's own cell, and when a drag last ended in the grid. SortableJS fires a click at the
  // end of a drag like any other pointer sequence, and that click would land on whichever chord the
  // reorder had just moved under the finger.
  const [cellRef, setCellRef] = useState(null);
  const draggedRef = useRef(0);

  // The bar opened in the bar editor: where it is, its slices as last edited there, and the one
  // selected. The slices are kept rather than re-read from the cell so a split the text can't tell
  // apart — two halves of one tie — still shows as two until one of them is given a chord.
  const [editing, setEditing] = useState(null);
  // The chord whose symbol is being retyped in the palette's field
  const [changing, setChanging] = useState(null);
  // ...and the one being moved to a gap
  const [movingChord, setMovingChord] = useState(null);
  // The bar whose chords are being dragged into a new order, if any
  const [reorderBar, setReorderBar] = useState(null);
  // Each line's row of bars, for the swap sortables
  const rowsRef = useRef([]);

  // The last letter-key write, and the grid as it stood before it. What makes a second press of the
  // same letter a correction rather than another chord — see handleLetter.
  const cycleRef = useRef(null);

  // The same, for the bar editor's Voicing button: which chord was last cycled and the grid it wrote,
  // so a run round the inversions is one undo rather than one per tap — see handleVoicing.
  const voicingRef = useRef(null);

  // The chart the part already carries, if any, as the grid edited here. Derived rather than passed
  // in: a grid prop would be a fresh array on every render of the form, and the reset effect below
  // keys off it. A chart left short of a bar line by a time-signature change opens with the rest of
  // its last bar missing, to be filled.
  const chartTimeSignature = song?.timeSignature;
  const existing = useMemo(
    () => chartDataToGrid(chart, getBeatsPerBar(chartTimeSignature), { openEnd: true }),
    [chart, chartTimeSignature],
  );
  // ...or, with no chart, the chords written out in the part, which the builder offers to start
  // from. A part made of words writes them over the words, so those rows only count as chords to
  // take where the chart is stored off the lyrics and nothing is replaced — see findChordLines.
  const importable = useMemo(
    () => (existing ? null : findChordLines(lyrics, { interleaved: isAttached })),
    [existing, isAttached, lyrics],
  );
  const [isImported, setIsImported] = useState(false);
  const [isImportDismissed, setIsImportDismissed] = useState(false);

  // The part's text, row by row, for the preview beside the grid — and whether there is anything in
  // it worth previewing. An instrumental of chord lines only has nothing the grid doesn't say, so the
  // toggle stays away there.
  const lyricsRows = useMemo(() => String(lyrics ?? '').split(/\r\n|\r|\n/), [lyrics]);
  const hasWords = useMemo(() => lyricsRows.some(isLyricsLine), [lyricsRows]);
  // Remembered between sessions, the way Rhythm and its style are: whether the words are up while a
  // chart is written is a way of working rather than a thing about this chart
  const [isLyricsPreview, toggleLyricsPreview] = useBooleanSetting(settingKeys.chordBarsBuilderLyrics, false);
  const showLyrics = hasWords && isLyricsPreview;
  // ...and whether playback may light a chord in them. The highlight is an index — the chart's nth
  // chord is the nth chord written over the words — which only means anything while the two are the
  // same chords in the same order, as they are when the chart was imported from them. A chart that
  // has been written past that point leaves the words there to read and nothing lit, rather than
  // timing a syllable it isn't playing.
  const followsWords = useMemo(() => lyricsMatchChart(lines, lyrics), [lines, lyrics]);

  // Every grid edit goes through commit, so it can be undone with the cursor put back where it was
  const { commit, undo, redo, reset, canUndo, canRedo } = useEditHistory(
    { lines, sections, countInEntry, position },
    { lines: setLines, sections: setSections, countInEntry: setCountInEntry, position: setPosition },
    ['lines', 'sections', 'countInEntry'],
  );

  // Destructured before anything memoises on it: `song` is assembled inline by the form, so it is
  // a new object on every render. Keying the clip on it would rebuild the clip each time, and a
  // new clip is what tells the player to tear down — playback would stop the moment it started.
  const { key: songKey, bpm, timeSignature = '4/4', category } = song || {};

  const chartKey = partKey || songKey;
  const isPartKeyDifferent = !!partKey && !!songKey && !isSameKey(partKey, songKey);
  const isPartBpmDifferent = !!bpm && !!songBpm && Number(bpm) !== Number(songBpm);
  const chords = useMemo(() => getDiatonicChords(chartKey), [chartKey]);
  // A melody bar under the cursor is written in notes, so the palette offers the key's notes: the
  // roots of its chords, in the same order
  const cursorMelody = isMelodyCell(lines[position.line]?.[position.bar]);
  const palette = useMemo(
    () =>
      cursorMelody
        ? chords.map((chord) => ({ ...chord, name: chord.name.match(/^[A-G](?:#|b)?/)?.[0] ?? chord.name, note: true }))
        : chords,
    [chords, cursorMelody],
  );
  const familyOf = useCallback((chord) => getChordFamily(chartKey, chord), [chartKey]);
  // Which palette chords are already in the chart, marked with a dot. The bass note and the
  // inversion mark both come off: they say how a chord is played, and this is showing which chords
  // are in the chart, so "E/G#" counts as an E. A melody bar's notes are not chords, so they don't count.
  const used = useMemo(
    () =>
      new Set(
        lines
          .flat()
          .filter((cell) => !isMelodyCell(cell))
          .flatMap(cellChords)
          .map((chord) => splitInversion(chord).symbol.split('/')[0]),
      ),
    [lines],
  );
  const bars = countGridBars(lines, sections);
  const secondsPerBar = getSecondsPerBar({ bpm, timeSignature });

  const beatsPerBar = getBeatsPerBar(timeSignature);
  // What plays: block chords where the style picked isn't written for this meter
  const rhythmId = toRhythmId(rhythmOverride || songRhythm, beatsPerBar);
  // The grid carries every chord's length while it is being edited; formatBarLines is where the
  // ones a bar can be read without come back out again, which is what the time signature is for
  const chartText = formatBarLines(lines, beatsPerBar, sections);
  // Bars with beats left missing; the chart can't be saved until they're filled
  const missingBars = lines.flat().filter((cell) => cellMissingBeats(cell, beatsPerBar) > 0).length;
  // The finest a chord can be cut in the bar editor
  const step = barStep(timeSignature);
  // The bar open in the bar editor. The slices it kept stand while they still write the cell as it
  // is; a cell changed since by anything else — an undo, a paste — is read afresh.
  const editingCell = editing ? lines[editing.line]?.[editing.bar] : undefined;
  const editingSlices = useMemo(() => {
    if (!editing || editingCell === undefined) return null;

    return sliceCell(editing.slices, beatsPerBar, { melody: isMelodyCell(editingCell) }) === editingCell
      ? editing.slices
      : readBarSlices(editingCell, beatsPerBar);
  }, [editing, editingCell, beatsPerBar]);
  const editingIndex = editingSlices ? Math.min(editing.selected, editingSlices.length - 1) : -1;
  // The chord in the selected slice — null for a tie or missing beats, which have none to change
  const selectedName = editingSlices?.[editingIndex]?.chord ?? null;
  // Which inversion the selected chord is written as, and how many it has. Empty for a chord there
  // is no cycle for — see inversionChords — which is what hides the button rather than greying it.
  const selectedInversions = selectedName ? inversionChords(selectedName) : [];
  const selectedInversion = selectedName ? inversionIndex(selectedName) : -1;
  const editingMelody = isMelodyCell(editingCell);
  // Only a bar with two chords or more has an order to change
  const canReorder = (editingSlices || []).filter(({ chord, missing }) => chord !== null && !missing).length > 1;
  // Somewhere for a moved chord to land: every run of missing beats in the chart, its own bar
  // included — a chord can be moved within the bar it is already in. Counted as runs rather than
  // bars, since one bar can be missing beats in two places ("C ? G ?").
  const gaps = useMemo(
    () =>
      lines.flatMap((cells, line) =>
        cells.flatMap((cell, bar) =>
          cellChordBeats(cell, beatsPerBar).flatMap(({ missing }, index) => (missing ? [{ line, bar, index }] : [])),
        ),
      ),
    [lines, beatsPerBar],
  );
  const hasGaps = gaps.length > 0;

  // What is sounding in the bar the cursor is on. Standing still, that is what the keyboard shows:
  // press a chord and its notes light up, move the cursor along the chart and it follows. Playing,
  // the player says what is sounding instead — the same keyboard, reading the same grid, so the
  // resting highlight and the moving one mean the same thing rather than two different things.
  // The last chord of that bar: the one just written, and the one still ringing at the bar line.
  // getSoundingChord hands back the whole cell, which for a shared bar is several symbols and not
  // one getChordMidiNotes can read.
  const cursorChord = cellChords(getSoundingChord(lines, position)).pop() || '';
  // With rhythm on, the bass is part of what the chord sounds as, so it lights up with it — the
  // resting highlight would otherwise sit an octave short of everything Play is about to show
  // A melody bar lights its one note.
  const cursorNotes = useMemo(() => {
    if (cursorMelody) {
      const note = cursorChord ? getMelodyNote(cursorChord) : null;

      return new Set(note === null ? [] : [note]);
    }

    const notes = getChordMidiNotes(cursorChord);

    return new Set(rhythmId ? [...notes, ...getChordRhythmBass(cursorChord)] : notes);
  }, [cursorChord, cursorMelody, rhythmId]);

  // Every bar before this line on the chart, so a position in the grid can be found by a flat index
  // and a flat index found back — which is how the playing bar is highlighted and how the cursor
  // tells the player where to come in.
  const barsBefore = lines.reduce((counts, cells) => [...counts, counts[counts.length - 1] + cells.length], [0]);
  const cursorBar = (barsBefore[position.line] ?? 0) + position.bar;

  // Gated rather than muted: the second argument releases the sampler, so a builder with the sound
  // off is not holding an instrument open waiting to be asked. The library always sounds.
  const play = useChordAudition(instrumentId, isOpen && (isAuditioning || isLibraryOpen), {
    rhythm: rhythmId,
    bpm,
    timeSignature,
  });
  // `melody` sounds the symbol as the single note a melody bar plays it as
  const audition = (chord, melody = false) => {
    if (isAuditioning) play(chord, { melody });
  };
  const player = useChordBarsPreview(lines, {
    bpm,
    timeSignature,
    category,
    instrumentId,
    isActive: isOpen,
    rhythm: rhythmId,
    // Play comes in where the cursor is. At the top of the chart that is bar 0, so nothing is lost
    // by it — but four lines down it is the difference between checking that line and sitting
    // through the three above it first.
    startBar: cursorBar,
    countInEntry,
    melody,
    effects: song?.chartEffects,
  });
  const { isPlaying, isLoading, midiJson, samples } = player;
  const tapper = useTapRhythm({ bpm, timeSignature });
  const isTapResult = tapper.phase === 'done';
  // Where the first tapped note lands, in beats into the bar: on the downbeat unless moved
  const [tapShift, setTapShift] = useState(0);
  // Whether the tapped bars are written as melody. Held apart from the cell while the result is up:
  // the bar may still be empty then, and an empty bar can't carry the mark.
  const [tapMelody, setTapMelody] = useState(false);
  // Chords put into the tapped result's slots while it is up, one per slot in order; null for one left open
  const [tapPicks, setTapPicks] = useState([]);
  // The slot tapped to be changed, by its index in tapPicks; null to fill the first open one
  const [tapTarget, setTapTarget] = useState(null);

  // Opens on the chart the part already carries, so the tool edits a chart rather than only ever
  // creating one. Everything else resets: a grid left over from the last part would be written
  // into this one on the first insert.
  useEffect(() => {
    if (!isOpen) return;

    const opened = openingGrid(existing);

    setLines(opened.lines);
    setSections(opened.sections);
    setCountInEntry(opened.countInEntry);
    setIsConfirmingClose(false);
    setPosition({ line: 0, bar: 0 });
    setEditing(null);
    setMovingChord(null);
    setCustom('');
    setCustomMode('closed');
    setIsLibraryOpen(false);
    setIsMixerOpen(false);
    setReorderBar(null);
    setIsImported(false);
    setIsImportDismissed(false);
    reset();
  }, [isOpen, existing, reset]);

  // The bar editor belongs to the bar the cursor is on: moving the cursor anywhere else closes it
  useEffect(() => {
    setEditing((open) => (open && (open.line !== position.line || open.bar !== position.bar) ? null : open));
  }, [position.line, position.bar]);

  // The open bar as the editor draws it, or null when the bar it was opened on is gone
  const openBar = editingSlices
    ? { line: editing.line, bar: editing.bar, slices: editingSlices, selected: editingIndex }
    : null;
  // Only the chart's first bar can come in during the count-in
  const isFirstBar = openBar?.line === 0 && openBar?.bar === 0;

  // The bars a tapped rhythm can run across: the open one and every one after it, to the end of the chart
  const tapBars = openBar
    ? lines
        .flatMap((cells, line) => cells.map((cell, bar) => ({ line, bar, cell })))
        .filter(({ line, bar }) => line > openBar.line || (line === openBar.line && bar >= openBar.bar))
        .slice(0, MAX_TAP_BARS)
    : [];

  // A tap session belongs to the bar it was started on, so anything that leaves that bar ends it
  const tapBar = isOpen && openBar ? `${openBar.line}:${openBar.bar}` : '';
  const { cancel: cancelTap } = tapper;

  useEffect(() => {
    cancelTap();
  }, [tapBar, cancelTap]);

  // Picks belong to the result they were made on; a new run or a cancel starts without them
  useEffect(() => {
    if (tapper.phase !== 'done') {
      setTapPicks([]);
      setTapTarget(null);
    }
  }, [tapper.phase]);

  // The tapped rhythm once the click has stopped, one entry per bar in tapBars (null where a bar is
  // left alone); null itself when nothing landed
  const tapped = (() => {
    if (tapper.phase !== 'done' || !openBar) return null;

    const result = slicesFromTaps(alignTaps(tapper.taps, tapShift), {
      bars: tapBars.map(({ cell }, i) => (i === 0 ? openBar.slices : readBarSlices(cell, beatsPerBar))),
      beatsPerBar,
      step,
      extra: tapPicks,
      markPicks: true,
    });
    // Tapped over the count-in, what comes before the first tap is the count, not a chord still ringing
    const [lead] = result[0] ?? [];

    if (tapper.countInBar && result[0]?.length > 1 && lead.chord === null && !lead.missing) {
      result[0] = [{ chord: null, rest: true, beats: lead.beats }, ...result[0].slice(1)];
    }

    return result.some(Boolean) ? result : null;
  })();

  // ...as the bar editor lists it: this bar, and each later one by its number on the chart
  const tapNumbered = (tapped ?? []).map((slices, i) => ({
    number: barsBefore[tapBars[i].line] + tapBars[i].bar + 1,
    slices,
  }));
  // Every slot a chord can be put into, in order; past the last one a pick has nowhere to show
  const tapSlots = tapNumbered.flatMap(({ number, slices }) =>
    (slices ?? []).flatMap(({ pick }, index) => (pick === undefined ? [] : [{ number, index, pick }])),
  );
  const firstOpen = tapPicks.findIndex((pick) => !pick);
  // The slot the next chord goes into: the one tapped, or else the first still open
  const tapAt = tapTarget ?? (firstOpen >= 0 ? firstOpen : tapPicks.length);
  const tapGap = tapSlots.find(({ pick }) => pick === tapAt);
  const tapSuggestion = tapped && {
    slices: tapped[0],
    following: tapNumbered.filter(({ slices }, i) => i > 0 && slices),
    gap: tapGap ?? null,
  };
  // While the result is up, a chord goes into its slot and never into the bar underneath it
  const tapFill = tapper.phase === 'done' ? { picks: tapPicks, at: tapGap ? tapAt : null } : null;

  const pickTapSlot = (pick) => {
    setTapTarget((at) => (at === pick ? null : pick));
    if (tapPicks[pick]) audition(tapPicks[pick], tapMelody);
  };

  // Shifting moves which taps land in which bar, so the slots change under the picks: one could end
  // up past the last slot. They start over instead.
  const shiftTaps = (delta) => {
    setTapShift((at) => Math.min(beatsPerBar - step, Math.max(0, at + delta * step)));
    setTapPicks([]);
    setTapTarget(null);
  };

  const startTap = () => {
    if (isPlaying) player.stop();

    // A bar that opens on a note still ringing from the one before starts its rhythm where that ends
    const [lead] = openBar.slices;
    const isHeld = openBar.slices.length > 1 && lead.chord === null && !lead.missing && !lead.rest;
    // The first bar of a chart that comes in during the count-in is the count-in, and is tapped over;
    // its rhythm starts where it comes in
    const overCount = isFirstBar && countInEntry;

    if (overCount) setTapShift(entryBeats(openBar.slices));
    else setTapShift(isHeld ? lead.beats : 0);
    setTapMelody(editingMelody);
    tapper.start(tapBars.length, { countInBar: overCount });
  };

  // Every bar in one commit, so one undo takes the whole rhythm back
  const applyTaps = () => {
    if (!tapped) return;

    const next = tapped.reduce((grid, slices, i) => {
      if (!slices) return grid;

      return replaceCell(grid, tapBars[i], sliceCell(slices, beatsPerBar, { melody: tapMelody }));
    }, lines);

    commit({ lines: next });
    tapper.cancel();
  };

  // Done keeps a tapped rhythm still on show, the way it keeps every other edit
  const closeBarEditor = () => {
    if (tapper.phase === 'done') applyTaps();
    setEditing(null);
  };

  const openBarEditor = (line, bar, selected = 0) => {
    const slices = readBarSlices(lines[line]?.[bar] ?? '', beatsPerBar);

    setEditing({ line, bar, slices, selected: Math.min(selected, slices.length - 1) });
  };

  // The beat of the count-in the first bar comes in on, or 0 to come in after it. Coming in
  // makes the beats before it a rest; going back to after it gives them back.
  const chooseEntry = (beat) => {
    const slices = beat > 0 || countInEntry ? setEntry(openBar.slices, Math.max(beat - 1, 0)) : openBar.slices;

    commit({
      lines: replaceCell(lines, openBar, sliceCell(slices, beatsPerBar, { melody: editingMelody })),
      countInEntry: beat > 0,
    });
    setEditing({ line: openBar.line, bar: openBar.bar, slices, selected: entryBeats(slices) > 0 ? 1 : 0 });
  };

  // One edit made in the bar editor, written back into its cell
  const editBar = (slices, selected) => {
    const text = sliceCell(slices, beatsPerBar, { melody: editingMelody });

    // A split tie writes the same hold it was, so there is nothing to undo
    if (text !== editingCell) commit({ lines: replaceCell(lines, openBar, text) });

    setEditing({ line: openBar.line, bar: openBar.bar, slices, selected });
  };

  const selectSlice = (index) => {
    const { chord } = openBar.slices[index] ?? {};

    setEditing({ ...openBar, selected: index });
    audition(chord ?? chordBefore(lines, openBar), editingMelody);
  };

  // The open bar played as single notes, or back to chords. One undo, like any edit in the editor.
  const toggleMelody = () => {
    if (!openBar) return;

    commit({ lines: replaceCell(lines, openBar, setCellMelody(editingCell, !editingMelody)) });
  };

  // The bar after this one in the chart, on the next line when this is the last on its own
  const nextBarOf = (grid, { line, bar }) => {
    if (grid[line]?.[bar + 1] !== undefined) return { line, bar: bar + 1 };

    return grid[line + 1]?.length ? { line: line + 1, bar: 0 } : null;
  };

  // The one place a chord is written. Taking a base rather than reading state is what lets the
  // letter keys replay a write from the snapshot below. A chord fills the cursor's bar, or with the
  // bar editor open, goes into its selected slice instead.
  const writeAt = (base, chord) => {
    if (base.tapFill) {
      const { picks, at } = base.tapFill;

      return {
        lines: base.lines,
        position: base.position,
        tapPicks: at === null ? picks : placePick(picks, at, chord),
      };
    }

    if (!base.openBar) return writeChord(base.lines, base.position, chord);

    const { selected } = base.openBar;
    const slices = setSliceChord(base.openBar.slices, selected, chord);
    const melody = isMelodyCell(base.lines[base.openBar.line]?.[base.openBar.bar]);
    const written = replaceCell(base.lines, base.openBar, sliceCell(slices, beatsPerBar, { melody }));
    const next = nextMissing(slices, selected);

    // Filling a gap moves on to the next one, so a run of notes can be typed straight in — over the
    // bar line too, once this bar has none left and the one after still has
    if (base.openBar.slices[selected]?.missing && !slices[next].missing) {
      const after = nextBarOf(written, base.openBar);
      const afterSlices = after && readBarSlices(written[after.line][after.bar], beatsPerBar);
      const gap = afterSlices ? afterSlices.findIndex(({ missing }) => missing) : -1;

      if (gap >= 0) {
        return { lines: written, position: after, editing: { ...after, slices: afterSlices, selected: gap } };
      }
    }

    return {
      lines: written,
      position: base.position,
      editing: { ...base.openBar, slices, selected: base.openBar.slices[selected]?.missing ? next : selected },
    };
  };

  const applyWrite = (written, options) => {
    // Held apart from the chart until the rhythm is applied
    if (written.tapPicks) {
      setTapPicks(written.tapPicks);
      setTapTarget(null);

      return;
    }

    commit(
      {
        lines: written.lines,
        // A chord written onto the last bar of the last line grows the chart by one, so the sections
        // beside it have to grow with it — see sectionsForLines
        sections: sectionsForLines(sections, written.lines.length),
        position: written.position,
      },
      options,
    );

    if (written.editing) setEditing(written.editing);
  };

  const handleChord = (chord) => {
    applyWrite(writeAt({ lines, position, openBar, tapFill }, chord));
    audition(chord, cursorMelody);
  };

  /**
   * A tap on a palette chord is the same question the chart asks, and it goes to the same toggle.
   * Auditioning on, the row is a keyboard: the chord sounds and the chart is left alone, so the key
   * can be tried out before any of it is written down. Off, the row is what it has always been —
   * the shortest way to write that chord in.
   *
   * The field behind the palette's + stays an insert in both modes. It is reached by typing a symbol out and
   * pressing Add, which says plainly enough what it is for, and preview-only there would leave no
   * way at all to write a chord the song's key doesn't offer.
   */
  const handlePaletteChord = (chord) => {
    if (isAuditioning) {
      audition(chord, cursorMelody);

      return;
    }

    handleChord(chord);
  };

  // Auditioning is for hearing the chart, not editing it, so turning it on shuts the bar editor
  const toggleAudition = () => {
    if (!isAuditioning) {
      setEditing(null);
      if (changing) closeCustom();
    }

    setIsAuditioning((on) => !on);
  };

  /**
   * A letter key writes the chord of that name from the song's own scale.
   *
   * Pressed again straight away it steps to the next chord sharing the letter rather than writing a
   * second one: the grid is rebuilt from the snapshot taken before the first press, so the cycle
   * replaces its own last write instead of adding to it. Letters do repeat — the diatonic chords
   * are spelled off a fixed chromatic scale rather than by degree, so F#/Gb comes out
   * Gb Abm Bbm B Db Ebm Fdim, where both Bbm and B answer to B — and a letter the key has nothing
   * for writes the plain triad, which is how a borrowed chord is reached without the field.
   *
   * The snapshot is only good while the grid and the cursor are still the ones it left behind,
   * which is checked by identity: every edit in chordBars returns a new array, so any other action
   * in between invalidates it without having to announce itself. A letter with only one chord to
   * offer never cycles at all, or pressing C twice would write one bar of C rather than two.
   */
  const handleLetter = (letter) => {
    const matches = palette.filter((chord) => chord.name[0].toUpperCase() === letter).map((chord) => chord.name);
    const options = matches.length > 0 ? matches : [letter];

    const cycle = cycleRef.current;
    const isCycling =
      options.length > 1 &&
      cycle?.letter === letter &&
      cycle.lines === lines &&
      cycle.position === position &&
      Date.now() - cycle.at < CYCLE_MS;

    const index = isCycling ? (cycle.index + 1) % options.length : 0;
    const before = isCycling ? cycle.before : { lines, position, openBar, tapFill };
    const written = writeAt(before, options[index]);

    // A cycle replaces its own last write, so one undo takes the whole cycle back
    applyWrite(written, { replace: isCycling });
    cycleRef.current = { letter, index, before, lines: written.lines, position: written.position, at: Date.now() };
    audition(options[index], cursorMelody);
  };

  // Only an open field closes. The field can blur again on its way out — unmounting a focused input
  // does that — and a second close must not restart the shrink. What was typed is left in place to
  // fade out with the field rather than blinking to the placeholder first; opening clears it.
  const closeCustom = () => {
    setCustomMode((mode) => (mode === 'open' ? 'closing' : mode));
    setChanging(null);
  };

  const openCustom = () => {
    setCustom('');
    setCustomMode('open');
  };

  /**
   * Enter writes the chord and leaves the field open and focused, so a run of chords the key doesn't
   * offer can be typed one after another. A tap on the button is a one-off, and folds the field back
   * into its + so the palette is one tidy block again.
   */
  const handleAddCustom = ({ keepOpen = false } = {}) => {
    const chord = custom.trim();

    if (!isChordToken(chord)) return;

    // Retyping the selected chord: the field always closes on one — there is no run of them to
    // type the way there is when chords are being added
    if (changing) {
      handleChord(chord);
      closeCustom();

      return;
    }

    handleChord(chord);
    // Staying open, the field empties for the next chord. Closing, the chord just written fades out
    // with it instead of being swapped for the placeholder mid-fade.
    if (keepOpen) setCustom('');
    else closeCustom();
  };

  // Retyping the selected chord's symbol. The palette's field opens on what is written now, so a
  // change starts from the chord rather than from an empty box.
  const startChange = () => {
    if (!selectedName) return;

    setChanging({ chord: selectedName });
    setCustom(selectedName);
    setCustomMode('open');
  };

  // Moving a chord to a gap: every run of missing beats in the chart is a target until one is tapped
  // or the mode is left
  const startMove = () => {
    if (!openBar || !selectedName) return;

    const from = { line: openBar.line, bar: openBar.bar, index: openBar.selected };

    setEditing(null);

    // With one gap there is nothing to pick: the mode would light up a single target and wait to be
    // told what it already knows, so the chord just goes there. Undo puts it back.
    if (gaps.length === 1) {
      commit({ lines: moveChord(lines, from, gaps[0], beatsPerBar) });

      return;
    }

    setMovingChord({ ...from, chord: selectedName });
  };

  const startReorder = () => {
    setReorderBar({ line: openBar.line, bar: openBar.bar });
    setEditing(null);
  };

  /**
   * Round the chord's inversions: C, then C/E, then C/G, then back.
   *
   * A slash chord is how an inversion has always been written, so this renames the chord and leaves
   * its timing alone. A run of taps is one undo, and the editor stays open so each can be heard.
   */
  const handleVoicing = () => {
    if (!openBar || !selectedName) return;

    const next = nextInversionChord(selectedName);

    if (!next) return;

    const { line, bar, selected } = openBar;
    const cycle = voicingRef.current;
    // Still the same chord, and nothing has touched the grid since — checked by identity, the way
    // handleLetter checks its own
    const isCycling = cycle?.lines === lines && cycle.line === line && cycle.bar === bar && cycle.index === selected;
    const slices = setSliceChord(openBar.slices, selected, next);
    const written = replaceCell(lines, openBar, sliceCell(slices, beatsPerBar, { melody: editingMelody }));

    commit({ lines: written }, { replace: isCycling });
    setEditing({ line, bar, slices, selected });
    voicingRef.current = { line, bar, index: selected, lines: written };
    audition(next, editingMelody);
  };

  /**
   * A tap in the grid is told apart by what it landed on: a chord symbol carries the index of the
   * chord it draws, and everything else in a cell is the bar itself.
   *
   * A tap moves the cursor; a second tap on the cursor's bar opens it in the bar editor, with the
   * chord it landed on selected. Auditioning on, whatever is tapped sounds and the editor stays shut.
   */
  const handleCellClick = (line, bar, event) => {
    // Read off the chord's own box: a tap on its symbol or its beat count is still a tap on the chord
    const chordEl = event?.target?.closest?.('[data-chord-index]');
    const index = Number(chordEl?.dataset?.chordIndex);
    const wasCursor = position.line === line && position.bar === bar;

    // Moving a chord: a tap on a gap drops it there, and a tap on anything else leaves the mode. The
    // cursor stays where it was either way — placing a chord is not navigating to one.
    if (movingChord) {
      const gapEl = event?.target?.closest?.('[data-gap-index]');

      if (gapEl) {
        commit({
          lines: moveChord(lines, movingChord, { line, bar, index: Number(gapEl.dataset.gapIndex) }, beatsPerBar),
        });
      }

      setMovingChord(null);

      return;
    }

    // Reordering: a tap in the bar is a drag finishing, and a tap anywhere else means done
    if (reorderBar) {
      if (line !== reorderBar.line || bar !== reorderBar.bar) setReorderBar(null);

      return;
    }

    // A drag through this cell ends in a click; the drop has already happened and what is under the
    // finger is not what was there when it started
    if (Date.now() - draggedRef.current < DROP_CLICK_MS) return;

    // With a tapped result up, a chord in the chart is one more way to fill its next box. Nothing else
    // in the chart answers a tap, since leaving the bar would throw the result away.
    if (tapFill) {
      const chord = Number.isInteger(index) && cellChordBeats(lines[line]?.[bar] ?? '', beatsPerBar)[index]?.chord;

      if (chord) {
        applyWrite(writeAt({ lines, position, openBar, tapFill }, chord));
        audition(chord, tapMelody);
      }

      return;
    }

    setPosition({ line, bar });

    // A rest is picked out by its own index; a tie or a hold has none and is the start of its bar
    const restIndex = Number(event?.target?.closest?.('[data-rest-index]')?.dataset?.restIndex);
    // ...and so are beats still waiting for a chord
    const gapIndex = Number(event?.target?.closest?.('[data-gap-index]')?.dataset?.gapIndex);
    let tapped = 0;

    if (Number.isInteger(index)) tapped = index;
    else if (Number.isInteger(restIndex)) tapped = restIndex;
    else if (Number.isInteger(gapIndex)) tapped = gapIndex;

    // Open, the bar editor follows a tap to whatever bar it lands on. Closed, it opens on a second
    // tap of the cursor's bar, so a finger that was only moving the cursor never lands in it. Never
    // while auditioning, when a tap is only for hearing.
    if (!isAuditioning) {
      // A gap is only ever there to be filled, so a tap on one opens it straight away
      if (openBar?.line === line && openBar?.bar === bar) setEditing({ ...openBar, selected: tapped });
      else if (openBar || wasCursor || Number.isInteger(gapIndex)) openBarEditor(line, bar, tapped);
    }

    // Off cellChordBeats rather than cellChords, which drops the ties: the index came off a box
    // drawn from the first, and a tie in the bar would shift every symbol after it in the second
    if (Number.isInteger(index)) {
      audition(cellChordBeats(lines[line]?.[bar] ?? '', beatsPerBar)[index]?.chord || '', isMelodyCell(lines[line]?.[bar]));

      return;
    }

    // Only what is written here. A held bar carries the chord ringing into it from an earlier bar,
    // and sounding that on a tap says the bar holds something of its own when the whole point of an
    // empty cell is that it doesn't — so an empty bar is silent, as is a rest.
    if (lines[line]?.[bar] === '' || Number.isInteger(restIndex)) return;

    audition(getSoundingChord(lines, { line, bar }), isMelodyCell(lines[line]?.[bar]));
  };

  // The cursor's bar when the cursor is on this line, otherwise the line's last
  const handleRemoveBar = (line) => {
    const onLine = position.line === line;
    const bar = onLine ? position.bar : lines[line].length - 1;

    commit({
      lines: removeBar(lines, line, bar),
      ...(onLine ? { position: { line, bar: Math.min(bar, lines[line].length - 2) } } : null),
    });
  };

  // Up to a full line, after the cursor's bar when the cursor is on this line, otherwise at the end
  const handleFillLine = (line) => {
    commit({ lines: fillLine(lines, line, position.line === line ? position.bar : undefined) });
  };

  // As long as the line it goes under and in its section, with the cursor on it ready to write in
  const handleInsertLine = (line) => {
    commit({
      lines: insertLine(lines, line, lines[line]?.length || DEFAULT_BARS_PER_LINE),
      sections: sectionsAfterDuplicateLine(sections, line),
      position: { line: line + 1, bar: 0 },
    });
    // An open bar below would be left pointing at the line that moved into its place
    setEditing(null);
  };

  const handleRemoveLine = (line) => {
    // Deleting the only line leaves an empty chart rather than no chart, so there is always
    // somewhere for the cursor to be — and with it, a single section for that line to be in
    const isLast = lines.length <= 1;
    const remaining = isLast ? createGrid(1, DEFAULT_BARS_PER_LINE) : removeLine(lines, line);

    commit({
      lines: remaining,
      sections: isLast ? [0] : sectionsAfterRemoveLine(sections, line),
      position: { line: Math.min(position.line, remaining.length - 1), bar: 0 },
    });
  };

  // Null when no line is empty, which is also what hides the button
  const withoutEmptyLines = (() => {
    const next = removeEmptyLines(lines, sections);

    return next.lines === lines ? null : next;
  })();

  const handleRemoveEmptyLines = () => {
    // The cursor stays on its own line where that line stays, and goes to the top where it was taken out
    const isWritten = (cells) => cells.some((cell) => cell !== '');
    const line = lines.slice(0, position.line).filter(isWritten).length;

    commit({
      ...withoutEmptyLines,
      position: isWritten(lines[position.line] ?? []) ? { line, bar: position.bar } : { line: 0, bar: 0 },
    });
    // An open bar below would be left pointing at a line that moved into another's place
    setEditing(null);
  };

  const handleDuplicateLine = (line) => {
    commit({
      lines: duplicateLine(lines, line),
      sections: sectionsAfterDuplicateLine(sections, line),
      // The copy is the line the user is about to change — a duplicate of four bars of Dm is only
      // worth making so the second one can become something else — so the cursor moves onto it
      position: { line: line + 1, bar: 0 },
    });
  };

  /**
   * A grid edit made by a drag: the new grid, and the remount that throws away what SortableJS
   * moved, in one render.
   *
   * The two have to land together. SortableJS has already taken the dragged node out of one row and
   * put it in another, so a render carrying the new lines but the old key leaves React reconciling
   * rows against children that are no longer theirs — insertBefore against a node that isn't there,
   * which is "the object can not be found here". Bumping the key in the same commit unmounts the
   * grid instead, and React builds it back from state without ever looking at the moved nodes.
   *
   * Batched by hand because React 17 doesn't: commit batches its own setters, and a setter called
   * after it is a second render — the crash happens in the first one.
   */
  const commitDrag = useCallback(
    (next) => {
      batchedUpdates(() => {
        commit(next);
        setGridKey((key) => key + 1);
      });
    },
    [commit],
  );

  // Reads nothing off the current grid: the sortable is built once per mount of the lines element,
  // so a handler closing over `lines` would go stale the moment a chord was written into it.
  const handleSortLines = useCallback(
    (from, to) => {
      commitDrag((current) => ({
        lines: moveLine(current.lines, from, to),
        sections: sectionsAfterMoveLine(current.sections, from, to),
        // The cursor holds onto the line it was on, wherever that line has moved to
        position: { ...current.position, line: lineAfterMove(current.position.line, from, to) },
      }));
    },
    [commitDrag],
  );

  // Scoped rather than the app's shared sortable: the parts list underneath this modal owns that
  // one, and taking it over would leave those parts undraggable once the modal closes.
  useEffect(() => {
    const sortable = createScopedSortable(linesRef, handleSortLines);

    return () => sortable?.destroy();
  }, [linesRef, handleSortLines]);

  const handleSortChords = useCallback(
    (from, to) => {
      draggedRef.current = Date.now();
      // For the reason handleSortLines takes commitDrag, one level further in: SortableJS moves the
      // dragged span in the DOM itself and the chords are keyed by index, so without the remount
      // React goes on believing the node it built for index 0 is still there and the bar draws in
      // an order the grid does not hold.
      commitDrag((current) => ({ lines: reorderChord(current.lines, current.position, from, to, beatsPerBar) }));
    },
    [beatsPerBar, commitDrag],
  );

  /**
   * Chords are dragged only in Reorder mode, and only within that bar (the cursor's). The symbol is
   * the handle, and there's no delay: in this mode a press on a chord can't mean anything else.
   */
  useEffect(() => {
    if (!reorderBar) return undefined;

    const sortable = createScopedSortable(cellRef, handleSortChords, {
      handle: null,
      draggable: '.cell-chord:not(.cell-chord--tie):not(.cell-chord--missing)',
      // Drawn by Sortable rather than by the browser, so the ghost matches the bars' own
      forceFallback: true,
    });

    return () => sortable?.destroy();
  }, [cellRef, handleSortChords, reorderBar]);

  /**
   * A bar dropped on another bar swaps with it; dropped on the slot at the end of a line, it moves
   * there. Applied a tick later: rebuilding the grid inside SortableJS's own drop destroys the row
   * sortables mid-drop, and the Swap plugin throws on the one it is still finishing.
   */
  const handleDropBar = useCallback((evt) => {
    const from = { line: Number(evt.from.dataset.line), bar: evt.oldIndex };
    const to = { line: Number(evt.to.dataset.line), bar: evt.newIndex };

    draggedRef.current = Date.now();

    // SortableJS moved nodes whether or not anything changed, so the grid is remounted either way
    setTimeout(() => {
      commitDrag((current) => {
        const isMove = to.bar >= (current.lines[to.line]?.length ?? 0);
        const next = isMove ? moveBar(current.lines, from, to.line) : swapBars(current.lines, from, to);

        if (next === current.lines) return {};

        // The cursor goes with the bar that was dragged
        return { lines: next, position: isMove ? { line: to.line, bar: next[to.line].length - 1 } : to };
      });
    }, 0);
  }, [commitDrag]);

  /**
   * Any bar can be dragged onto another, on any line, to swap the two, or onto a line's end slot to
   * move it there. Off while a bar's chords are being reordered, a chord is being moved, or a tapped
   * result is up and the bars under it can't shift.
   */
  useEffect(() => {
    const sortables = rowsRef.current.slice(0, lines.length).map((row) =>
      createScopedSortable(row, null, {
        group: 'chord-bars-cells',
        swap: true,
        swapClass: 'cell--swap-target',
        draggable: '.cell',
        handle: null,
        filter: '.cell--end-slot',
        preventOnFilter: false,
        // A mouse has nothing to disambiguate — a press on a bar is either a click or a drag, and
        // which one it is shows in whether it moves — so it waits for nothing. A finger does: the
        // same press could be starting a scroll, which is what the delay below leaves room for.
        delay: DRAG_DELAY_MS,
        delayOnTouchOnly: true,
        // Sortable clamps touchStartThreshold to 1 whenever it drags natively, and then divides it
        // by the device pixel ratio — which floors to 0 on any retina screen, so the first pixel of
        // movement cancelled the press and the bar had to be held perfectly still to pick it up.
        // Its own fallback drag keeps the threshold as written, and draws a truer ghost besides.
        forceFallback: true,
        touchStartThreshold: DRAG_JITTER_PX,
        // Sortable marks the bar chosen the instant it is pressed, and the end-of-line slots used
        // to be drawn off that — so every tap on a bar flashed them. They key off the drag proper
        // now (see .sortable-ghost in the stylesheet), which this is what holds back.
        fallbackTolerance: DRAG_TRAVEL_PX,
        disabled: Boolean(reorderBar) || Boolean(movingChord) || isTapResult,
        onEnd: handleDropBar,
      }),
    );

    return () => sortables.forEach((sortable) => sortable?.destroy());
  }, [gridKey, lines.length, reorderBar, movingChord, isTapResult, handleDropBar]);

  // Offered only on an empty grid, so taking it never throws away something already written
  const canImport =
    !!importable && !isImported && !isImportDismissed && lines.every((cells) => cells.every((cell) => cell === ''));

  // A mode or a prompt owns the builder, and the grid can't be changed out from under it
  const isEditLocked =
    Boolean(reorderBar) || canImport || Boolean(changing) || Boolean(movingChord);

  const handleImport = () => {
    const imported = wrapLines(importable.lines, importable.lines.map(() => 0));

    setLines(imported.lines);
    setSections(imported.sections);
    setCountInEntry(false);
    setPosition({ line: 0, bar: 0 });
    setIsImported(true);
    // The chart starts here: undoing past it would leave isImported claiming a replacement
    reset();
  };

  const handleInsert = () => {
    onInsert(
      gridToChartData(lines, sections, beatsPerBar, { countInEntry }),
      { instrumentId: instrumentOverride, rhythm: rhythmOverride },
      { replaceChords: isImported },
    );
    onClose();
  };

  // Only once there is one stored to take off — what is in the grid right now is backed out of with
  // Cancel.
  const canRemove = Boolean(onRemove) && Boolean(existing);

  const handleRemove = () => {
    onRemove();
    onClose();
  };

  // The button asks before it does it: one tap turns it into "Sure?", the next takes the chart off
  const [isConfirmingRemove, confirmRemove] = useConfirmClick(handleRemove);

  // A tap on the backdrop is easy to make by accident, so one that would lose changes asks first.
  // The modal is mounted per open, so the stored instrument and rhythm are what it opened on.
  const [isConfirmingClose, setIsConfirmingClose] = useState(false);
  const opening = useMemo(
    () => ({
      ...openingGrid(existing),
      instrumentOverride: getChartInstrumentOverride({ chartInstrumentId: storedInstrumentId }),
      rhythmOverride: storedRhythm,
    }),
    [existing, storedInstrumentId, storedRhythm],
  );
  const hasChanges = !_.isEqual({ lines, sections, countInEntry, instrumentOverride, rhythmOverride }, opening);
  const canSave = Boolean(chartText) && missingBars === 0;

  const handleBackdropClick = () => (hasChanges ? setIsConfirmingClose(true) : onClose());

  const copiedChart = useCopiedChart();
  const [isCopied, setIsCopied] = useState(false);

  useEffect(() => {
    if (!isCopied) return undefined;

    const timer = setTimeout(() => setIsCopied(false), COPIED_MS);

    return () => clearTimeout(timer);
  }, [isCopied]);

  const handleCopy = () => {
    copyChart(chartText);
    setIsCopied(true);
  };

  // Not offered back over the chart it was copied from
  const canPaste = Boolean(copiedChart) && copiedChart !== chartText;
  // The import prompt doesn't lock it: an empty part is where a paste is most wanted
  const isPasteLocked = Boolean(reorderBar) || Boolean(changing) || Boolean(movingChord);

  // Replaces the whole grid, as one undoable edit
  const handlePaste = () => {
    const parsed = parseBarLines(copiedChart);

    if (!parsed?.lines?.length) return;

    commit({ ...wrapLines(parsed.lines, parsed.sections), position: { line: 0, bar: 0 } });
    setIsImported(false);
    setIsImportDismissed(true);
  };

  // One bar, copied off the cursor's and pasted onto it — from the bar editor or with ⌘C / ⌘V
  const copiedBar = useCopiedBar();
  const [isBarCopied, setIsBarCopied] = useState(false);
  const cursorCell = lines[position.line]?.[position.bar];
  // A bar's beats are read against the meter, so one copied under another doesn't paste
  const canPasteBar = copiedBar?.beatsPerBar === beatsPerBar && cursorCell !== undefined;

  useEffect(() => {
    if (!isBarCopied) return undefined;

    const timer = setTimeout(() => setIsBarCopied(false), COPIED_MS);

    return () => clearTimeout(timer);
  }, [isBarCopied]);

  const handleCopyBar = () => {
    if (cursorCell === undefined) return;

    copyBar(cursorCell, beatsPerBar);
    setIsBarCopied(true);
  };

  const handlePasteBar = () => {
    if (!canPasteBar || copiedBar.cell === cursorCell) return;

    commit({ lines: replaceCell(lines, position, copiedBar.cell) });
  };

  // Play lives in the header: a chart of any length puts the transport below the grid, and checking
  // the line you just wrote shouldn't mean scrolling away from it.
  const transport = (
    <>
      <ModalAction
        icon={isPlaying ? <StopIcon className="solid small" /> : <PlayIcon className="solid" />}
        label={player.playLabel}
        active={isPlaying}
        busy={player.playBusy}
        progress={player.playProgress}
        disabled={!player.canPlay}
        onClick={isPlaying ? player.stop : player.play}
      />
      <ModalAction
        icon={isCopied ? <CheckIcon /> : <CopyIcon />}
        label={isCopied ? t('dialogs.chordBars.copied') : t('dialogs.chordBars.copyChart')}
        disabled={!chartText}
        onClick={handleCopy}
      />
      {canPaste && (
        <ModalAction
          icon={<PasteIcon />}
          label={t('dialogs.chordBars.pasteChart')}
          disabled={isPasteLocked}
          onClick={handlePaste}
        />
      )}
      <ModalAction
        icon={<SlidersIcon />}
        label={t('player.volume')}
        active={isMixerOpen}
        onClick={() => setIsMixerOpen((open) => !open)}
      />
    </>
  );

  /**
   * The chart's own keyboard: a letter writes its chord, Delete clears a bar (H holds with the bar
   * editor open), space plays, Enter sends the cursor back to the top.
   *
   * On the document rather than on an element, because there is no element to hang it on — the
   * cells are buttons and so is every control beside them, so focus is wherever the last tap left
   * it. The guards are Dropdown's, for its reasons: a field being typed into keeps its keys, and an
   * open line menu keeps the a/r/d/x it already answers to.
   *
   * Rebound every render deliberately. It reads the grid, the cursor and the open bar on every press,
   * and a dependency list naming them all would rebind on the same renders anyway — one
   * addEventListener on a document node is cheaper than the staleness the alternative invites.
   */
  useEffect(() => {
    // The volume sheet covers the grid, and its sliders take the arrows
    if (!isOpen || isMixerOpen) return undefined;

    const handleKeyDown = (event) => {
      const { tagName, isContentEditable } = event.target;
      const isTyping = isContentEditable || tagName === 'INPUT' || tagName === 'TEXTAREA' || tagName === 'SELECT';
      const key = event.key.toLowerCase();
      const historyStep = getHistoryShortcut(event);

      // Undo and redo, held down or not. A field being typed into keeps its own text undo.
      if (historyStep) {
        if (isTyping || isEditLocked) return;

        event.preventDefault();

        if (historyStep === 'redo') redo();
        else undo();

        return;
      }

      // Copy and paste the cursor's bar. A field, or text someone has selected, keeps its own.
      if ((event.metaKey || event.ctrlKey) && !event.altKey && !event.shiftKey && (key === 'c' || key === 'v')) {
        if (isTyping || isEditLocked || tapper.phase !== 'idle') return;
        if (key === 'c' && window.getSelection()?.toString()) return;
        if (document.querySelector('.mg-dropdown__menu') || isLibraryOpen || document.querySelector('.BottomSheet')) {
          return;
        }

        event.preventDefault();

        if (key === 'c') handleCopyBar();
        else if (!event.repeat) handlePasteBar();

        return;
      }

      // A held key would write twenty chords, or start and stop the chart twenty times
      if (event.repeat || event.metaKey || event.ctrlKey || event.altKey) return;

      // A prompt is up; nothing should happen behind it
      if (canImport) return;

      // Moving owns it the same way reordering does, and for the same reason: nothing is written
      // while a chord is looking for somewhere to go
      if (movingChord) {
        if (event.key === 'Enter') {
          event.preventDefault();
          setMovingChord(null);
        }

        return;
      }

      // Reordering owns the keyboard: Enter is done (Escape too, below), and nothing else writes
      if (reorderBar) {
        if (event.key === 'Enter') {
          event.preventDefault();
          setReorderBar(null);
        }

        return;
      }

      if (isTyping) return;

      // A tap session takes Space and Shift as taps and Enter as stop, and nothing else writes while
      // it counts
      if (tapper.isRunning) {
        if (event.key === ' ' || event.key === 'Shift') {
          event.preventDefault();
          tapper.tap(event.timeStamp);
        }

        if (event.key === 'Enter') {
          event.preventDefault();
          tapper.stop();
        }

        return;
      }

      // A line menu is open and owns the keyboard until it closes. Matched on Dropdown's own class
      // rather than on role="menu", which the navbar's profile menu carries in the document at all
      // times — that selector reads as "a menu is open" and is in fact always true.
      if (document.querySelector('.mg-dropdown__menu')) return;
      // The same for a sheet, the Chord Library among them. Over the Rhythm and Instrument sheets
      // Space still plays and stops, and nothing else writes.
      if (isLibraryOpen || document.querySelector(KEYBOARD_SHEET)) return;
      if (event.key !== ' ' && document.querySelector('.BottomSheet')) return;

      // Backspace alongside Delete: the key a Mac keyboard labels "delete" sends Backspace, and it
      // is the one under the finger
      if (event.key === 'Delete' || event.key === 'Backspace') {
        event.preventDefault();

        // With the bar editor open it is the selected chord that goes, not the whole bar
        if (openBar) {
          const { slices, selected } = removeSlice(openBar.slices, openBar.selected);

          editBar(slices, selected);
        } else {
          commit({ lines: clearCell(lines, position) });
        }

        return;
      }

      // Play comes in at the cursor, so this lands on the next play, not the bar sounding now
      if (event.key === 'Enter') {
        if (event.target.closest?.('.ChordBarsTransport')) return;

        event.preventDefault();
        setPosition({ line: 0, bar: 0 });

        return;
      }

      if (event.key === ' ') {
        // Except on the transport's own toggles, where space is how a focused button is pressed
        if (event.target.closest?.('.ChordBarsTransport')) return;

        // Focus is on a cell as often as not, and a cell is a button — without this it clicks
        event.preventDefault();

        if (isPlaying) player.stop();
        else if (player.canPlay) player.play();

        return;
      }

      const letter = event.key.toUpperCase();

      if (/^[A-G]$/.test(letter)) {
        event.preventDefault();
        handleLetter(letter);
      }

      // Hold, as the bar editor's button: only there, and only in a bar of two or more. On a lone
      // chord it would only ever empty the bar, which Delete already does.
      if (letter === 'H' && openBar?.slices.length > 1) {
        event.preventDefault();

        const { slices, selected } = holdSlice(openBar.slices, openBar.selected);

        editBar(slices, selected);
      }

      // Rest, the same way
      if (letter === 'R' && openBar) {
        event.preventDefault();

        const { slices, selected } = restSlice(openBar.slices, openBar.selected);

        editBar(slices, selected);
      }
    };

    document.addEventListener('keydown', handleKeyDown);

    return () => document.removeEventListener('keydown', handleKeyDown);
  });

  /**
   * Escape leaves whatever the builder is in the middle of — reordering, moving a chord, the bar
   * editor — and goes no further. SongForm cancels the whole part edit on a document-level Escape, so
   * this listens on window in the capture phase to get there first. With none of those open, Escape
   * passes through as before.
   */
  useEffect(() => {
    if (
      !isOpen ||
      isLibraryOpen ||
      isMixerOpen ||
      (!reorderBar && !openBar && !movingChord && !changing)
    ) {
      return undefined;
    }

    const handleEscape = (event) => {
      if (event.key !== 'Escape') return;
      // The Rhythm sheet takes this one; the builder doesn't know it is open
      if (document.querySelector('.BottomSheet')) return;

      event.preventDefault();
      event.stopPropagation();
      // This listens in the capture phase, so it gets there before the field's own Escape — which
      // means closing the field is this handler's job whenever it is the one that is open. The bar
      // editor under it stays, so the next Escape is the one that closes that.
      if (changing) {
        closeCustom();

        return;
      }

      // ...and the same for a tap session, which leaves the bar editor it was started from open
      if (tapper.phase !== 'idle') {
        tapper.cancel();

        return;
      }

      setReorderBar(null);
      setMovingChord(null);
      setEditing(null);
    };

    window.addEventListener('keydown', handleEscape, true);

    return () => window.removeEventListener('keydown', handleEscape, true);
  });

  return (
    <>
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      onBackdropClick={handleBackdropClick}
      title={partLabel ? t('dialogs.chordBars.titleWithPart', { part: partLabel }) : t('dialogs.chordBars.title')}
      className="ChordBarsModal"
      actions={transport}
      evenFooter
      footer={
        <>
          {canRemove && (
            <ModalButton
              variant={isConfirmingRemove ? 'danger' : 'secondary'}
              className="remove"
              onClick={confirmRemove}
              aria-label={
                isConfirmingRemove ? t('dialogs.chordBars.removeConfirmLabel') : t('dialogs.chordBars.removeChart')
              }
              title={isConfirmingRemove ? t('dialogs.chordBars.removeConfirm') : t('dialogs.chordBars.removeChart')}
            >
              <TrashIcon />
            </ModalButton>
          )}
          <ModalButton variant="secondary" onClick={onClose}>
            {t('common.cancel')}
          </ModalButton>
          <ModalButton disabled={!canSave} onClick={handleInsert}>
            {existing
              ? t('dialogs.chordBars.updateChart')
              : isImported && !isAttached
                ? t('dialogs.chordBars.replaceChords')
                : t('dialogs.chordBars.saveChart')}
          </ModalButton>
        </>
      }
      sheet={
        <ChordBarsMixer
          isOpen={isMixerOpen}
          onClose={() => setIsMixerOpen(false)}
          player={player}
          idPrefix="chord-bars"
          drums
        />
      }
    >
      <div className="columns">
        <div className="col col--chart">
          {/* One fact per pill rather than a run of them separated by dots. They are read by
              glancing for the one you want — the tempo, the bar count — and a boundary you can
              see beats a dot you have to parse. */}
          <div className="chart-stats">
            <span className="context">
              {isPartKeyDifferent ? (
                <>
                  <span className="stat">{t('dialogs.chordBars.songKey', { key: spellKey(songKey) })}</span>
                  <span className="stat stat--part-key">{t('dialogs.chordBars.partKey', { key: spellKey(partKey) })}</span>
                </>
              ) : (
                <span className="stat">
                  {chartKey ? t('dialogs.chordBars.key', { key: chartKey }) : t('dialogs.chordBars.noKey')}
                </span>
              )}
              {isPartBpmDifferent ? (
                <>
                  <span className="stat">{t('dialogs.chordBars.songBpm', { bpm: songBpm })}</span>
                  <span className="stat stat--part-bpm">{t('dialogs.chordBars.partBpm', { bpm })}</span>
                </>
              ) : bpm ? (
                <span className="stat">{bpm} BPM</span>
              ) : null}
              <span className="stat">{timeSignature}</span>
            </span>
            <span className="length">
              <span className="stat">
                <Trans i18nKey="dialogs.chordBars.bars" count={bars} components={{ b: <strong /> }} />
              </span>
              {secondsPerBar ? <span className="stat">~{formatSeconds(bars * secondsPerBar)}</span> : null}
            </span>
          </div>

          {/* The scale's chords and a + for anything else, all one centred run that wraps. The
              field only exists once + is tapped, so at rest the palette is chords and nothing
              else. */}
          <div className="palette">
            {chords.length === 0 && (
              <p className="no-key">{t('dialogs.chordBars.noKeyHint')}</p>
            )}

            <div className="chord-buttons">
              {palette.map((chord) => (
                <button
                  key={chord.name}
                  type="button"
                  className={cx('chord-btn', !chord.note && getChordQuality(chord.name), {
                    'chord-btn--used': !chord.note && used.has(chord.name),
                  })}
                  title={chord.note ? t('dialogs.chordBars.noteTitle', { name: chord.name }) : `${chord.roman} — ${chord.name}`}
                  onClick={() => handlePaletteChord(chord.name)}
                >
                  {chord.name}
                </button>
              ))}

              {isCustomOpen ? (
                <div className={cx('custom-chord', { 'custom-chord--closing': customMode === 'closing' })}>
                  {/* The box is this wrapper rather than the input, so the input's text can fade
                      on its own while the border and surface it sits in go on shrinking */}
                  <span
                    className="field"
                    // The shrink finishing is what hands back to the real +. Only the wrapper's
                    // own animation counts: the text and the + inside it report here too.
                    onAnimationEnd={(e) => {
                      if (e.target !== e.currentTarget) return;
                      setCustomMode((mode) => (mode === 'closing' ? 'closed' : mode));
                    }}
                  >
                    <input
                      type="text"
                      value={custom}
                      placeholder={t('dialogs.chordBars.chordPlaceholder')}
                      aria-label={
                        changing
                          ? t('dialogs.chordBars.newSymbolFor', { chord: changing.chord })
                          : t('dialogs.chordBars.customChord')
                      }
                      autoFocus
                      onChange={(e) => setCustom(e.currentTarget.value)}
                      // Tapped away from with nothing in it, the field folds back into its +. With
                      // something typed it stays, so a tap on Add isn't lost to the blur before it.
                      onBlur={() => {
                        if (!custom.trim()) closeCustom();
                      }}
                      onKeyDown={(e) => {
                        if (e.key === 'Escape') {
                          // Only the field closes — not the modal, and not the part edit SongForm
                          // cancels on a document-level Escape
                          e.preventDefault();
                          e.stopPropagation();
                          closeCustom();

                          return;
                        }

                        if (e.key !== 'Enter') return;
                        // The modal sits inside the song form; Enter here must not submit the song
                        e.preventDefault();
                        handleAddCustom({ keepOpen: true });
                      }}
                    />
                    <Icon icon={<AddChordIcon />} className="field-plus" aria-hidden="true" />
                  </span>
                  <button
                    type="button"
                    className="add"
                    aria-label={changing ? t('dialogs.chordBars.changeChord') : t('dialogs.chordBars.addChord')}
                    disabled={!isChordToken(custom.trim())}
                    onClick={() => handleAddCustom()}
                  >
                    <Icon icon={changing ? <CheckIcon /> : <PlusIcon />} />
                  </button>
                </div>
              ) : (
                <button
                  type="button"
                  className="chord-btn chord-btn--add"
                  title={t('dialogs.chordBars.addAnyChord')}
                  aria-label={t('dialogs.chordBars.addAnyChord')}
                  onClick={openCustom}
                >
                  <Icon icon={<AddChordIcon />} />
                </button>
              )}

              {chords.length > 0 && (
                <button
                  type="button"
                  className="chord-btn chord-btn--more"
                  title={t('dialogs.chordBars.chordLibrary')}
                  aria-label={t('dialogs.chordBars.chordLibrary')}
                  onClick={() => setIsLibraryOpen(true)}
                >
                  <Icon icon={<LibraryIcon />} />
                </button>
              )}
            </div>
          </div>

          <div
            className={cx('grid', {
              'grid--focused': reorderBar,
              'grid--reordering': reorderBar,
              'grid--moving': Boolean(movingChord),
            })}
          >
            {reorderBar && (
              <div className="mode-hint">
                <span>{t('dialogs.chordBars.reorderHint')}</span>
                <button type="button" onClick={() => setReorderBar(null)}>
                  {t('common.done')}
                </button>
              </div>
            )}
            {movingChord && (
              <div className="mode-hint">
                <span>
                  <Trans
                    i18nKey="dialogs.chordBars.moveHint"
                    values={{ chord: movingChord.chord }}
                    components={{ chord: <span className="chord-name" /> }}
                  />
                </span>
                <button type="button" onClick={() => setMovingChord(null)}>
                  {t('common.done')}
                </button>
              </div>
            )}
            <div className="grid-lines" key={gridKey} ref={setLinesRef}>
              {lines.map((cells, line) => (
                <div className={cx('grid-line', sectionClass(sections, line))} key={line}>
                  <Icon icon={<DragIcon />} className="drag-handle" title={t('dialogs.chordBars.dragToReorder')} />
                  <div
                    className="cells"
                    data-line={line}
                    ref={(el) => {
                      rowsRef.current[line] = el;
                    }}
                  >
                    {cells.map((cell, bar) => {
                      const isCursor = position.line === line && position.bar === bar;
                      const isOpenBar = openBar?.line === line && openBar?.bar === bar;
                      const isMelody = isMelodyCell(cell);

                      return (
                        <button
                          key={bar}
                          type="button"
                          className={cx('cell', {
                            'cell--cursor': isCursor,
                            'cell--focus-target':
                              reorderBar?.line === line && reorderBar?.bar === bar,
                            'cell--missing': cellMissingBeats(cell, beatsPerBar) > 0,
                            'cell--hold': cell === '',
                            'cell--melody': isMelody,
                            'cell--sounding': player.currentBar === barsBefore[line] + bar,
                          })}
                          // A held bar is the chord before it still ringing, so it is labelled as
                          // such rather than left as an unexplained blank
                          title={cellTitle(t, cell, beatsPerBar)}
                          style={{ ...cellFit(cell, beatsPerBar), ...cellShade(cell, beatsPerBar, familyOf) }}
                          // Only the cursor's cell is sortable — see the effect that builds it
                          ref={isCursor ? setCellRef : undefined}
                          onClick={(event) => handleCellClick(line, bar, event)}
                        >
                          {/* A bar played as single notes says so in its corner */}
                          {isMelody && (
                            <span className="melody-badge" aria-label={t('player.melody')}>
                              ♪
                            </span>
                          )}
                          {cell === '' ? (
                            <span className="hold-mark" aria-hidden="true" />
                          ) : (
                            // Each chord as wide as the beats it holds, so a shared bar is drawn the
                            // way it will be played rather than as an even row of symbols
                            cellChordBeats(cell, beatsPerBar).map(({ chord, beats: held, missing, rest }, index, all) =>
                              // Beats left missing: a stretch as wide as they are, counted the way a
                              // chord's beats are, until a chord is written into it
                              missing ? (
                                <span
                                  // eslint-disable-next-line react/no-array-index-key
                                  key={index}
                                  className={cx('cell-chord', 'cell-chord--missing', {
                                    'cell-chord--selected': isOpenBar && openBar.selected === index,
                                  })}
                                  // Which gap a tap landed on, to move a chord to or to select
                                  data-gap-index={index}
                                  style={{ flexGrow: held ?? undefined }}
                                >
                                  {held > 0 && <span className="cell-beats">{formatBeats(held)}</span>}
                                </span>
                              ) : rest ? (
                                // Silence, counted like a chord. No chord-index: it is not a chord to
                                // move or reorder, though it is selected like one in the bar editor.
                                <span
                                  // eslint-disable-next-line react/no-array-index-key
                                  key={index}
                                  className={cx('cell-chord', 'cell-chord--rest', {
                                    'cell-chord--selected': isOpenBar && openBar.selected === index,
                                  })}
                                  data-rest-index={index}
                                  style={{ flexGrow: held ?? undefined }}
                                >
                                  <span className="cell-chord-stack">
                                    <span className="cell-chord-name">
                                      <RestMark
                                        countIn={countInEntry && line === 0 && bar === 0 && index === 0 ? held : 0}
                                      />
                                    </span>
                                    {all.length > 1 && held > 0 && <span className="cell-beats">{formatBeats(held)}</span>}
                                  </span>
                                </span>
                              ) : // A chord tied in from the bar before draws the rule a held bar
                              // draws, across the beats it is still holding. No chord-index on it:
                              // there is no symbol here to remove, and the one it holds lives next door.
                              chord === null ? (
                                <span
                                  // eslint-disable-next-line react/no-array-index-key
                                  key={index}
                                  className="cell-chord cell-chord--tie"
                                  style={{ flexGrow: held ?? undefined }}
                                >
                                  <span className="hold-mark" aria-hidden="true" />
                                </span>
                              ) : (
                              <span
                                // eslint-disable-next-line react/no-array-index-key
                                key={index}
                                className={cx('cell-chord', familyOf(chord) && `chord-family--${familyOf(chord)}`, {
                                  'cell-chord--note': isMelody,
                                  'cell-chord--selected': isOpenBar && openBar.selected === index,
                                  'cell-chord--moving':
                                    movingChord?.line === line &&
                                    movingChord?.bar === bar &&
                                    movingChord?.index === index,
                                })}
                                data-chord-index={index}
                                style={{ flexGrow: held ?? undefined }}
                              >
                                {/* Symbol and beat count stacked, and the stack only as wide as the
                                    symbol, so the number sits under the chord it belongs to rather
                                    than under the whole width of the beats it is counting. */}
                                <span className="cell-chord-stack">
                                  <span className="cell-chord-name">
                                    {splitInversion(chord).symbol}
                                    {splitInversion(chord).inversion > 0 && (
                                      <sup>{INVERSION_ORDINALS[splitInversion(chord).inversion]}</sup>
                                    )}
                                  </span>
                                  {/* How long it lasts, in the unit the chart is counted in. Written
                                      on every bar holding more than one chord rather than only on the
                                      uneven ones — a bar being even is the thing worth being able to
                                      see, and it cannot be read off two numbers that aren't there. */}
                                  {all.length > 1 && held > 0 && <span className="cell-beats">{formatBeats(held)}</span>}
                                </span>
                              </span>
                              ),
                            )
                          )}
                        </button>
                      );
                    })}
                    {/* Drop a dragged bar here to move it to the end of this line. The stylesheet
                        only shows it mid-drag, and a full line has no end to move one to. */}
                    {cells.length < MAX_BARS_PER_LINE && <div className="cell cell--end-slot" aria-hidden="true" />}
                  </div>
                  {/* The same menu the parts list uses, for the same reason: a line has more to
                      offer than fits beside it as buttons, and the ones that reorder or copy it
                      want naming rather than guessing at from a glyph. */}
                  <Dropdown
                    portal
                    className="line-menu"
                    menuClassName="ChordBarsLineMenu"
                    align="right"
                    ariaLabel={t('dialogs.chordBars.lineOptionsLabel', { number: line + 1 })}
                    title={t('dialogs.chordBars.lineOptions')}
                    trigger={<Icon icon={<EllipsisVertical />} className="options-icon" />}
                  >
                    <DropdownItem
                      shortcut="a"
                      disabled={cells.length >= MAX_BARS_PER_LINE}
                      onClick={() => commit({ lines: addBar(lines, line) })}
                    >
                      <Icon className="line-menu__icon" icon={<PlusIcon />} />
                      <span>{t('dialogs.chordBars.addBar')}</span>
                    </DropdownItem>
                    <DropdownItem
                      shortcut="f"
                      disabled={cells.length >= MAX_BARS_PER_LINE}
                      onClick={() => handleFillLine(line)}
                    >
                      <Icon className="line-menu__icon" icon={<FillLineIcon />} />
                      <span>{t('dialogs.chordBars.fillLine')}</span>
                    </DropdownItem>
                    <DropdownItem
                      shortcut="r"
                      disabled={cells.length <= 1}
                      onClick={() => handleRemoveBar(line)}
                    >
                      <Icon className="line-menu__icon" icon={<RemoveBarIcon />} />
                      <span>{t('dialogs.chordBars.removeBar')}</span>
                    </DropdownItem>
                    <DropdownItem shortcut="d" onClick={() => handleDuplicateLine(line)}>
                      <Icon className="line-menu__icon" icon={<CopyIcon />} />
                      <span>{t('dialogs.chordBars.duplicateLine')}</span>
                    </DropdownItem>
                    <DropdownItem shortcut="l" onClick={() => handleInsertLine(line)}>
                      <Icon className="line-menu__icon" icon={<AddChordIcon />} />
                      <span>{t('dialogs.chordBars.addLineBelow')}</span>
                    </DropdownItem>
                    {/* Where one instrument's lines end and the next one's begin. Nothing to open
                        above the first line — there is no line up there to be divided from. */}
                    <DropdownItem
                      shortcut="s"
                      disabled={line === 0}
                      onClick={() => commit({ sections: toggleSection(sections, line) })}
                    >
                      <Icon className="line-menu__icon" icon={<SectionIcon />} />
                      <span>
                        {opensSection(sections, line)
                          ? t('dialogs.chordBars.mergeSection')
                          : t('dialogs.chordBars.startSection')}
                      </span>
                    </DropdownItem>
                    <DropdownDivider />
                    <DropdownItem className="line-menu__delete" shortcut="x" onClick={() => handleRemoveLine(line)}>
                      <Icon className="line-menu__icon" icon={<TrashIcon />} />
                      <span>{t('dialogs.chordBars.deleteLine')}</span>
                    </DropdownItem>
                  </Dropdown>
                  {/* The open bar, under the line it sits in so the bar tapped stays where it was */}
                  {openBar?.line === line && (
                    <BarEditor
                      slices={openBar.slices}
                      selected={openBar.selected}
                      barNumber={barsBefore[line] + openBar.bar + 1}
                      beatsPerBar={beatsPerBar}
                      step={step}
                      heldChord={chordBefore(lines, openBar)}
                      familyOf={familyOf}
                      melody={tapper.phase === 'idle' ? editingMelody : tapMelody}
                      onToggleMelody={tapper.phase === 'idle' ? toggleMelody : () => setTapMelody((on) => !on)}
                      onSelect={selectSlice}
                      onChange={editBar}
                      onChangeChord={startChange}
                      onVoicing={selectedInversions.length > 1 ? handleVoicing : undefined}
                      voicingLabel={INVERSION_ORDINALS[selectedInversion] || t('dialogs.barEditor.root')}
                      onReorder={canReorder ? startReorder : undefined}
                      onMove={hasGaps && selectedName ? startMove : undefined}
                      isCopied={isBarCopied}
                      onCopy={handleCopyBar}
                      onPaste={canPasteBar ? handlePasteBar : undefined}
                      canPaste={canPasteBar && copiedBar.cell !== cursorCell}
                      onDone={closeBarEditor}
                      countIn={
                        isFirstBar
                          ? { beat: countInEntry ? 1 + entryBeats(openBar.slices) : 0, onChoose: chooseEntry }
                          : undefined
                      }
                      tapping={{
                        phase: tapper.phase,
                        isRunning: tapper.isRunning,
                        beat: tapper.beat,
                        countInBar: tapper.countInBar,
                        canTap: tapper.canTap,
                        suggestion: tapSuggestion,
                        onStart: startTap,
                        onTap: tapper.tap,
                        onStop: tapper.stop,
                        shift: tapShift,
                        onPickSlot: pickTapSlot,
                        onShift: shiftTaps,
                        onCancel: tapper.cancel,
                      }}
                    />
                  )}
                </div>
              ))}
            </div>

            <div className="grid-footer">
              {/* Scrolls sideways under undo/redo once there are more of these than fit */}
              <div className="footer-actions">
                <button
                  type="button"
                  className="add-line"
                  onClick={() =>
                    commit({
                      lines: addLine(lines, lines[lines.length - 1]?.length || DEFAULT_BARS_PER_LINE),
                      sections: sectionsAfterAddLine(sections),
                    })
                  }
                >
                  <Icon icon={<PlusIcon />} /> {t('dialogs.chordBars.addLine')}
                </button>

                {/* Every short line made up to four bars, so the chart reads as an even grid */}
                {lines.some((cells) => cells.length < MAX_BARS_PER_LINE) && (
                  <button type="button" className="fill-lines" onClick={() => commit({ lines: fillLines(lines) })}>
                    <Icon icon={<FillLineIcon />} /> {t('dialogs.chordBars.fillLines')}
                  </button>
                )}

                {/* Only while some line has nothing written in it */}
                {withoutEmptyLines && (
                  <button type="button" className="remove-empty-lines" onClick={handleRemoveEmptyLines}>
                    <Icon icon={<TrashIcon />} /> {t('dialogs.chordBars.removeEmptyLines')}
                  </button>
                )}
              </div>

              <div className="history">
                <button
                  type="button"
                  className="history-btn"
                  aria-label={t('dialogs.chordBars.undo')}
                  title={t('dialogs.chordBars.undo')}
                  disabled={!canUndo || isEditLocked}
                  onClick={undo}
                >
                  <Icon icon={<UndoIcon />} />
                </button>
                <button
                  type="button"
                  className="history-btn"
                  aria-label={t('dialogs.chordBars.redo')}
                  title={t('dialogs.chordBars.redo')}
                  disabled={!canRedo || isEditLocked}
                  onClick={redo}
                >
                  <Icon icon={<RedoIcon />} />
                </button>
              </div>
            </div>
          </div>
        </div>

        <div className="col col--player">
          {/* Top of the column, over the piano: the words are what the chart is being written
              against, so they read beside the grid rather than under everything it is heard with */}
          {showLyrics && (
            <LyricsPreview
              rows={lyricsRows}
              currentChord={followsWords ? player.currentChord : -1}
              currentChordSeconds={followsWords ? player.currentChordSeconds : 0}
            />
          )}

          {midiJson && <MidiPiano midiJson={midiJson} activeNotes={isPlaying ? player.activeNotes : cursorNotes} />}

          <ChordBarsTransport
            player={player}
            audition={{ on: isAuditioning, onToggle: toggleAudition }}
            rhythm={{ choice: rhythmOverride || songRhythm, onChoose: pickRhythm, timeSignature }}
            rhythmDefault={songRhythm}
            lyrics={
              hasWords
                ? {
                    on: isLyricsPreview,
                    onToggle: toggleLyricsPreview,
                    title: t('dialogs.chordBars.showLyrics'),
                  }
                : undefined
            }
          >
            <InstrumentRow
              instrumentId={instrumentId}
              onSelect={pickInstrument}
              defaultId={songInstrumentId}
              recallLast={false}
              player={player}
              browser={{
                downloadedIds: samples.downloadedIds,
                playingId: isPlaying ? instrumentId : undefined,
                loadingId: isLoading || samples.status === 'loading' ? instrumentId : undefined,
                progress: { loaded: samples.loaded, total: samples.total },
              }}
            />
          </ChordBarsTransport>

          {missingBars > 0 && (
            <p className="missing-hint">
              {t('dialogs.chordBars.missingHint', { count: missingBars })}
            </p>
          )}
        </div>
      </div>
    </Modal>

    <ChordLibrarySheet
      songKey={chartKey}
      isOpen={isOpen && isLibraryOpen}
      onClose={closeLibrary}
      onPlay={play}
      onAdd={handleChord}
    />

    {/* Asked once, on an empty grid: start from the chords the part already has written out.
        Portalled like Modal is, so it lands after the builder in the body and sits on top of it. */}
    {createPortal(
      <PromptModal
        isOpen={isOpen && canImport}
        onClose={() => setIsImportDismissed(true)}
        primaryText={t('dialogs.chordBars.importChords')}
        secondaryText={t('dialogs.chordBars.startEmpty')}
        onPrimaryAction={handleImport}
        onSecondaryAction={() => setIsImportDismissed(true)}
        message={
          <div className="chord-import-prompt">
            <p>{t('dialogs.chordBars.importPrompt')}</p>
            {!isAttached && <p>{t('dialogs.chordBars.importReplaces')}</p>}
          </div>
        }
      />,
      document.body,
    )}

    {/* A chart that can't be saved yet (bars short of beats) can only be kept editing or dropped */}
    {createPortal(
      <PromptModal
        isOpen={isOpen && isConfirmingClose}
        onClose={() => setIsConfirmingClose(false)}
        message={canSave ? t('dialogs.chordBars.unsavedClose') : t('dialogs.chordBars.unsavedIncomplete')}
        primaryText={canSave ? t('dialogs.chordBars.saveChanges') : t('dialogs.chordBars.discardChanges')}
        onPrimaryAction={canSave ? handleInsert : onClose}
        secondaryText={canSave ? t('dialogs.chordBars.discardChanges') : t('dialogs.chordBars.keepEditing')}
        onSecondaryAction={canSave ? onClose : undefined}
        isDestructive={!canSave}
      />,
      document.body,
    )}
    </>
  );
};

ChordBarsModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onInsert: PropTypes.func.isRequired,
  onRemove: PropTypes.func,
  partLabel: PropTypes.string,
  partKey: PropTypes.string,
  songBpm: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  song: PropTypes.shape({
    key: PropTypes.string,
    bpm: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    timeSignature: PropTypes.string,
    category: PropTypes.string,
    chartInstrumentId: PropTypes.string,
    chartRhythm: PropTypes.string,
    chartEffects: PropTypes.object,
  }),
  lyrics: PropTypes.string,
  isAttached: PropTypes.bool,
  chart: PropTypes.shape({ events: PropTypes.array }),
  instrumentId: PropTypes.string,
  rhythm: PropTypes.string,
  melody: PropTypes.shape({
    midiJson: PropTypes.object,
    instrumentId: PropTypes.string,
    semitones: PropTypes.number,
    atBar: PropTypes.number,
  }),
};

export default ChordBarsModal;
